/// <amd-module name="Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/ConfigQueries/IterationsHelper" />
import { Iteration, IterationsQuery } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { localeComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import * as Resources from "Widgets/AnalyticsSprintBurndown/Resources";

export const currentIterationId = "@CurrentIteration";

export function isSymbolicCurrentIteration(iterationSk: string): boolean {
    return currentIterationId === iterationSk;
}

/** Gets a list of iterations scoped to the supplied team  */
export async function getIterations(pageContext: IVssPageContext, projectId: string, teamId: string): Promise<Iteration[]> {
    const queryCache = pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
    const maxNumberOfIterationsToQuery = 100; // A high limit, as we are querying for a config scenario, not general rendering.
    const iterations = await queryCache.getCacheableQueryResult(IterationsQuery.onTeam(projectId, teamId, maxNumberOfIterationsToQuery));
    return iterations;
}

export function selectActualCurrentIteration(iterations: Iteration[]): Iteration {
    const currentDate = new Date();
    let selectedIteration = {} as Iteration;
    const index = iterations.findIndex(iteration => {
        return (
            !isSymbolicCurrentIteration(iteration.IterationSK) &&
            new Date(iteration.StartDateTimeOffset) < currentDate &&
            new Date(iteration.EndDateTimeOffset) > currentDate
        );
    });
    if (index >= 0) {
        selectedIteration = iterations[index];
    }

    // Where there is no iteration bounded by the present, select a historical match.
    if (index < 0) {
        // We can only choose from iterations which have already occurred, and which have a defined end date
        const historicalIterations = iterations.filter(iteration => {
            return (
                !isSymbolicCurrentIteration(iteration.IterationSK) &&
                new Date(iteration.StartDateTimeOffset) < currentDate &&
                iteration.EndDateTimeOffset != null
            );
        });

        if (historicalIterations.length > 0) {
            // Select the last match in terms of end date (and in event of a tie, last alpha-sorted iteration path).
            // This replicates the NodeComparison sort preference on service side which chooses last end date
            historicalIterations.sort((a, b) => {
                const diff = new Date(a.EndDateTimeOffset).getTime() - new Date(b.EndDateTimeOffset).getTime();

                if (diff !== 0) {
                    return diff;
                }
                if (diff === 0) {
                    return localeComparer(a.IterationPath, b.IterationPath);
                }
                return diff;
            });

            selectedIteration = historicalIterations[historicalIterations.length - 1];
        }
    }
    return selectedIteration;
}

/** Makes a symbolic "currentIteration", populated with actual dates, which the widget and config know they have permission to override(unlike dates in general). */
export function populateSymbolicCurrentIteration(iterations: Iteration[]): Iteration {
    const concreteCurrentIteration = selectActualCurrentIteration(iterations);
    const currentIteration = {
        ...concreteCurrentIteration,
        IterationName: Resources.CurrentIterationLabel,
        IterationSK: currentIterationId,
        StartDateTimeOffset: concreteCurrentIteration.StartDateTimeOffset,
        EndDateTimeOffset: concreteCurrentIteration.EndDateTimeOffset
    } as Iteration;
    return currentIteration;
}
